/**
 * Dev-store blob PUT (T-08). Stands in for the server-side committed-media
 * home (`MARK_MEDIA_ROOT/{tenant_id}/{project_ref}/media/{capture_id}/{sha256}.{ext}`,
 * PIN-1g→1h-MEDIA-ROOT) whose live transport is 1l's (gated). This module
 * implements the SHAPE the retry-unit (T-06) depends on now: the key
 * scheme, idempotent content-addressed PUT, and a human-debuggable
 * directory listing — an in-memory Map is a faithful stand-in because the
 * real store is server-side (not persisted in the browser either way).
 */

const store = new Map<string, { bytes: Uint8Array; mime: string }>();

export interface BlobPutResult {
  key: string;
  bytes: number;
  alreadyPresent: boolean;
}

/** `media/{capture_id}/{sha256}.{ext}` — the project-relative `media_ref`
 * wire format (design §5.2). */
export function mediaKey(capture_id: string, sha256: string, ext: string): string {
  return `media/${capture_id}/${sha256}.${ext}`;
}

/**
 * Codec-honest extension mapping for the two-variant media-role vocabulary
 * (§2.4). Deliberately a closed map — an unrecognized mime throws rather
 * than guessing an extension.
 */
const MIME_EXT: Record<string, string> = {
  "audio/mp4": "m4a",
  "audio/m4a": "m4a",
  "audio/webm": "webm",
  "image/jpeg": "jpg",
  "image/png": "png",
};

export function extFromMime(mime: string): string {
  // PARAMETER-tolerant lookup: a live MediaRecorder stamps its blob with a
  // codec-QUALIFIED type — "audio/webm;codecs=opus" on Chrome/Android,
  // "audio/mp4;codecs=..." on iOS — never the bare container type. The file
  // extension maps to the CONTAINER, which is fully determined by the media
  // type BEFORE any ";" parameters, so normalize them off (and case-fold; mime
  // types are case-insensitive) before the lookup. Still codec-honest — the
  // container is real, only the codec/param suffix is dropped for the ext.
  // (Without this, a real on-device recording throws here and strands the
  // whole capture on the "Transcribing…" leg — the params never reached the map.)
  const base = mime.split(";", 1)[0].trim().toLowerCase();
  const ext = MIME_EXT[base];
  if (!ext) {
    throw new Error(`blob-store: no codec-honest extension mapping for mime "${mime}"`);
  }
  return ext;
}

export async function putBlob(
  capture_id: string,
  sha256: string,
  ext: string,
  mime: string,
  blob: Blob,
): Promise<BlobPutResult> {
  const key = mediaKey(capture_id, sha256, ext);
  const bytes = new Uint8Array(await blob.arrayBuffer());
  const existing = store.get(key);
  if (existing) {
    if (!bytesEqual(existing.bytes, bytes)) {
      // Content address collision: same sha256-derived key, different bytes.
      // Should be unreachable in practice (sha256 is the key), but a
      // silent overwrite here would violate idempotent-PUT — fail loudly.
      throw new Error(`blob-store: content-address collision at "${key}"`);
    }
    return { key, bytes: bytes.length, alreadyPresent: true };
  }
  store.set(key, { bytes, mime });
  return { key, bytes: bytes.length, alreadyPresent: false };
}

export function getBlob(key: string): { bytes: Uint8Array; mime: string } | undefined {
  return store.get(key);
}

/** Human-debuggable directory listing (T-08 done-check). */
export function listBlobs(): string[] {
  return [...store.keys()].sort();
}

export function _resetBlobStoreForTests(): void {
  store.clear();
}

function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}
